import { call, put, select } from 'redux-saga/effects';
import axios from 'axios';
import {
  fetchModuleAccessRequest,
  fetchModuleAccessSuccess,
  fetchModuleAccessFailure,
  fetchCompanyModuleAccessRequest,
  fetchCompanyModuleAccessSuccess,
  fetchCompanyModuleAccessFailure,
  updateModuleAccessRequest,
  updateModuleAccessSuccess,
  updateModuleAccessFailure,
} from '../../slices/moduleAccessSlice';
import { TRACKER_API_BASE } from '../../../BaseUrl/BaseUrl';

function authHeaders() {
  const Token = window.localStorage.getItem('auth-token');
  return { headers: { 'x-auth-token': `${Token}` } };
}

function jsonHeaders() {
  const Token = window.localStorage.getItem('auth-token');
  return {
    headers: {
      'Content-Type': 'application/json',
      'x-auth-token': `${Token}`,
    },
  };
}

/** Build POST body: `product` / `campaign` modules → `product_flag` / `campaign_flag`. */
export function buildModuleAccessPostBody(detail) {
  if (!detail?.company_id) return null;
  const modules = detail.modules || [];

  const flag = (name) => Boolean(modules.find((m) => m.module_name === name)?.access);
  return {
    company_id: detail.company_id,
    product_flag: flag('product'),
    campaign_flag: flag('campaign'),
    lead_flag: flag('lead'),
    order_flag: flag('order'),
    vendor_flag: flag('vendor'),
    discount_flag: flag('discount'),
    employees_flag: flag('employees'),
    designation_flag: flag('designation'),
    routes_flag: flag('routes'),
    packages_flag: flag('packages'),
    unit_flag: flag('unit'),
    invoice_flag: flag('invoice'),
    region_master_flag: flag('region_master'),
  };
}

/** Super-admin list: `data` is an object with numeric keys; see ModuleAccess page. */
export const fetchModuleAccessAPI = () =>
  axios.get(`${TRACKER_API_BASE}/module-access?page=${1}&limit=${10}`, authHeaders());

export const postModuleAccessAPI = (body) => axios.post(`${TRACKER_API_BASE}/module-access`, body, jsonHeaders());

export function* fetchModuleAccessSaga(action) {
  try {
    yield put(fetchModuleAccessRequest());
    const { page = 1, limit = 10, search } = action.payload || {};
    const searchParam =
      search && String(search).trim() ? `&searchText=${encodeURIComponent(String(search).trim())}` : '';

    const response = yield call(
      axios.get,
      `${TRACKER_API_BASE}/module-access?page=${page}&limit=${limit}${searchParam}`,
      authHeaders()
    );
    const moduleData = response?.data?.data;
    yield put(fetchModuleAccessSuccess(moduleData));
  } catch (error) {
    yield put(
      fetchModuleAccessFailure(error?.response?.data?.meta?.message || error?.response?.data?.message || error.message)
    );
  }
}

export const fetchCompanyModuleAccessAPI = (companyId) =>
  axios.get(`${TRACKER_API_BASE}/module-access/company-modules`, {
    ...authHeaders(),
    params: { company_id: companyId },
  });

export function* fetchCompanyModuleAccessSaga(action) {
  const companyId = action.companyId ?? action.payload?.companyId;
  if (!companyId) {
    yield put(fetchCompanyModuleAccessFailure('Missing company id'));
    return;
  }
  try {
    yield put(fetchCompanyModuleAccessRequest());
    const response = yield call(fetchCompanyModuleAccessAPI, companyId);
    const detail = response?.data?.data;
    yield put(fetchCompanyModuleAccessSuccess(detail));
  } catch (error) {
    yield put(
      fetchCompanyModuleAccessFailure(
        error?.response?.data?.meta?.message || error?.response?.data?.message || error.message
      )
    );
  }
}

export function* updateModuleAccessSaga() {
  const detail = yield select((s) => s.moduleAccess.companyModuleDetail);
  const body = buildModuleAccessPostBody(detail);
  if (!body) {
    yield put(updateModuleAccessFailure('No company selected'));
    return;
  }
  try {
    yield put(updateModuleAccessRequest());
    const response = yield call(postModuleAccessAPI, body);
    const message = response?.data?.meta?.message || 'Module access updated';
    yield put(updateModuleAccessSuccess(message));
  } catch (error) {
    yield put(
      updateModuleAccessFailure(error?.response?.data?.meta?.message || error?.response?.data?.message || error.message)
    );
    return;
  }
  try {
    const listRes = yield call(fetchModuleAccessAPI);
    const moduleData = listRes?.data?.data;
    yield put(fetchModuleAccessSuccess(moduleData));
  } catch {
    /* optional: table refresh failed; POST already succeeded */
  }
}
